/**
 * Owner: Masa Bayu
 * File: PrivateAuthorCharts.tsx
 * Date:Time: 2026-10-23 08:45
 * Description: Carta dashboard PRIVAT §467-C — garis Prestasi Jualan (SVG,
 *   animasi lukisan + tooltip titik) daripada `monthly` hub, dan donut Hasil
 *   Mengikut Mata Wang daripada `revenueByCurrency`. Tiada siri rekaan: bila
 *   belum ada pesanan berbayar, kad memaparkan keadaan kosong yang jujur
 *   (bukan 10 titik kosong dan bukan lencana DEMO).
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { formatMarketplacePriceOrUnknown } from '../../home/marketplace/marketplace-locale';
import type { AuthorMoneyText, AuthorSeriesPoint } from './private-author-view';
import type { PrivateAuthorLabels } from './private-author-labels';

const W = 560;
const H = 220;
const PAD_X = 16;
const PAD_TOP = 14;
const PAD_BOTTOM = 30;

const CURRENCY_TONES = ['var(--qxp-f1)', 'var(--qxp-f2)', 'var(--qxp-f3)', 'var(--qxp-f4)'];

function toneFor(i: number): string {
  return CURRENCY_TONES[i % CURRENCY_TONES.length] ?? 'var(--qxp-f1)';
}

export interface SalesLineChartProps {
  readonly la: PrivateAuthorLabels;
  readonly series: readonly AuthorSeriesPoint[];
  readonly currency: string | null;
}

export function SalesLineChart({ la, series, currency }: SalesLineChartProps): ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...series.map((p) => p.minorUnits), 1);
  const step = series.length > 1 ? (W - PAD_X * 2) / (series.length - 1) : 0;
  const yOf = (v: number): number => H - PAD_BOTTOM - (v / max) * (H - PAD_TOP - PAD_BOTTOM);
  const points = series.map((p, i) => ({ x: PAD_X + step * i, y: yOf(p.minorUnits), p }));
  const line = points
    .map((pt, i) => `${i === 0 ? 'M' : 'L'}${String(pt.x)},${String(pt.y)}`)
    .join(' ');
  const first = points[0];
  const last = points[points.length - 1];
  const area =
    first === undefined || last === undefined
      ? ''
      : `${line} L${String(last.x)},${String(H - PAD_BOTTOM)} L${String(first.x)},${String(H - PAD_BOTTOM)} Z`;
  const active = hover === null ? undefined : points[hover];
  const amountOf = (minorUnits: number): string =>
    formatMarketplacePriceOrUnknown(minorUnits, currency, la.priceUnknown);

  return (
    <section className="qxp__card qxp__chart" aria-label={la.chartSalesAria}>
      <div className="qxp__card-head">
        <h2 className="qxp__card-title">{la.chartSalesTitle}</h2>
        <div className="qxp__card-tools">
          <span className="qxp__metric">{la.chartMetric}</span>
        </div>
      </div>
      {series.length === 0 ? (
        <p className="qxp__empty">{la.chartEmpty}</p>
      ) : (
        <svg
          className="qxp__line"
          viewBox={`0 0 ${String(W)} ${String(H)}`}
          role="img"
          aria-label={la.chartSalesAria}
        >
          <defs>
            <linearGradient id="qxpArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(47,128,255,.24)" />
              <stop offset="100%" stopColor="rgba(47,128,255,0)" />
            </linearGradient>
          </defs>
          <path className="qxp__line-area" d={area} fill="url(#qxpArea)" />
          <path className="qxp__line-path" d={line} />
          {points.map((pt, i) => (
            <g key={pt.p.month}>
              <circle
                className={`qxp__line-dot${hover === i ? ' qxp__line-dot--on' : ''}`}
                cx={pt.x}
                cy={pt.y}
                r={hover === i ? 5.5 : 3.5}
              />
              <rect
                x={pt.x - step / 2}
                y={0}
                width={step === 0 ? W : step}
                height={H}
                fill="transparent"
                tabIndex={-1}
                aria-label={`${pt.p.label} · ${amountOf(pt.p.minorUnits)} · ${String(pt.p.orders)}`}
                onMouseEnter={() => {
                  setHover(i);
                }}
                onMouseLeave={() => {
                  setHover(null);
                }}
              />
              <text className="qxp__line-x" x={pt.x} y={H - 8} textAnchor="middle">
                {pt.p.label}
              </text>
            </g>
          ))}
          {active !== undefined ? (
            <g className="qxp__tip">
              <rect
                x={Math.min(Math.max(active.x - 52, 2), W - 106)}
                y={Math.max(active.y - 40, 2)}
                width="104"
                height="30"
                rx="7"
              />
              <text
                x={Math.min(Math.max(active.x - 52, 2), W - 106) + 52}
                y={Math.max(active.y - 40, 2) + 19}
                textAnchor="middle"
              >
                {`${active.p.label} · ${amountOf(active.p.minorUnits)}`}
              </text>
            </g>
          ) : null}
        </svg>
      )}
    </section>
  );
}

export interface CurrencyDonutProps {
  readonly la: PrivateAuthorLabels;
  readonly rows: readonly AuthorMoneyText[];
}

export function CurrencyDonut({ la, rows }: CurrencyDonutProps): ReactElement {
  const total = rows.reduce((sum, row) => sum + row.minorUnits, 0);
  if (total <= 0) {
    return (
      <section className="qxp__card qxp__donut-card" aria-label={la.currencyTitle}>
        <div className="qxp__card-head">
          <h2 className="qxp__card-title">{la.currencyTitle}</h2>
        </div>
        <p className="qxp__empty">{la.currencyEmpty}</p>
      </section>
    );
  }

  const slices = rows.map((row, i) => {
    const pct = Math.round((row.minorUnits / total) * 100);
    return { ...row, pct: Math.max(1, pct), tone: toneFor(i) };
  });
  let cursor = 0;
  const stops = slices
    .map((slice) => {
      const from = cursor;
      cursor += slice.pct;
      return `${slice.tone} ${String(from)}% ${String(cursor)}%`;
    })
    .join(', ');
  const top = slices[0];

  return (
    <section className="qxp__card qxp__donut-card" aria-label={la.currencyTitle}>
      <div className="qxp__card-head">
        <h2 className="qxp__card-title">{la.currencyTitle}</h2>
      </div>
      <div className="qxp__donut-body">
        <div
          className="qxp__donut"
          style={{ background: `conic-gradient(${stops})` }}
          role="img"
          aria-label={slices.map((s) => `${s.currency} ${String(s.pct)}%`).join(', ')}
        >
          <span>
            <strong>{top === undefined ? la.noData : `${String(top.pct)}%`}</strong>
            <em>{top === undefined ? la.noData : top.currency}</em>
          </span>
        </div>
        <ul className="qxp__legend">
          {slices.map((slice) => (
            <li key={slice.currency}>
              <i style={{ background: slice.tone }} aria-hidden="true" />
              <span>{slice.currency}</span>
              <strong>{slice.text}</strong>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
